import { fabric } from "fabric";
import type {
  Gradient,
  ITextOptions,
  Pattern,
  TextOptions,
} from "fabric/fabric-impl";
import { adjustElementTopWithPageBreak } from "../renderer/fabric-renderer-utils";
import { defaults } from "../state/defaults";
import { BorderedGroup } from "./bordered-objects";

export interface ITextareaOptions
  extends fabric.IObjectOptions,
    Pick<
      ITextOptions,
      | "fill"
      | "fontSize"
      | "fontWeight"
      | "fontFamily"
      | "textAlign"
      | "lineHeight"
      | "lockScalingY"
      | "editable"
      | "stroke"
      | "strokeWidth"
      | "text"
    > {
  id?: string;
  pageHeight: number;
  pageBreakMargin: number;
}

/**
 * Provides functionality for multi-line text rendering and layout.
 *
 * This class handles the recalculation of text lines, their positions,
 * and managing page breaks if a certain height is exceeded.
 */
export class Textarea extends BorderedGroup implements ITextareaOptions {
  type = "textarea";
  pageHeight = 0;
  pageBreakMargin = 0;
  fill: string | Pattern | Gradient = defaults.textColor;
  fontSize = defaults.fontSize;
  fontWeight: string | number = defaults.fontWeight;
  fontFamily = defaults.fontFamily;
  textAlign = defaults.textAlign;
  lineHeight = defaults.lineHeight;
  lockScalingY: boolean | undefined;
  editable: boolean | undefined;
  stroke = defaults.stroke;
  strokeWidth = defaults.strokeWidth;
  text = "";

  private static readonly INITIAL_HEIGHT = 10;

  constructor(text: string = "", options?: TextOptions, id?: string) {
    super([], options, true, id);
    this.text = text;
    this.recalculate();
  }

  recalculate(): void {
    this._objects = [];
    let lines = this.getLinesObjects();

    const lineHeights = calculateLineHeights(lines, this.lineHeight ?? 1);
    this.height = lineHeights.reduce(
      (total, height) => total + height,
      Textarea.INITIAL_HEIGHT,
    );

    this.top = adjustElementTopWithPageBreak(
      this.top ?? 0,
      lineHeights[0],
      this.pageHeight,
      this.pageBreakMargin,
    );

    lines = this.updateLinePositions(lines, lineHeights);
    for (const line of lines) {
      this.add(line);
    }
  }

  private getLinesObjects(): fabric.Object[] {
    const element = createLineElement(this, this.text ?? "");
    return element.textLines.map((lineText) =>
      createLineElement(this, lineText ?? ""),
    );
  }

  private updateLinePositions(
    lines: fabric.Object[],
    lineHeights: number[],
  ): fabric.Object[] {
    const containerTop = this.top ?? 0;
    const containerHeight = this.height ?? 0;
    const restPageHeight = this.pageHeight - containerTop;

    let accumulatedHeight = 0;
    lines.forEach((obj, lineIndex) => {
      this.adjustLinePosition(obj, accumulatedHeight);
      accumulatedHeight = this.checkForPageBreak(
        accumulatedHeight + lineHeights[lineIndex],
        lineHeights,
        lineIndex,
        restPageHeight,
      );
    });

    // The position needs to be updated for cases when lines hit on page
    // breaks and padding have been added to the top of some elements.
    const heightDiff = accumulatedHeight - containerHeight;
    if (heightDiff) {
      this.height = accumulatedHeight;
      this.adjustLinesTopPosition(lines, heightDiff);
    }

    return lines;
  }

  private adjustLinesTopPosition(
    objects: fabric.Object[],
    heightDiff: number,
  ): void {
    objects.forEach((obj) => {
      obj.top = (obj.top ?? 0) - heightDiff / 2;
    });
  }

  private adjustLinePosition(
    obj: fabric.Object,
    accumulatedHeight: number,
  ): void {
    if (this.width && this.height) {
      obj.set({
        top: accumulatedHeight - this.height / 2,
        left: -this.width / 2,
        width: this.width,
      });
    }
  }

  private checkForPageBreak(
    accumulatedHeight: number,
    lineHeights: number[],
    lineIndex: number,
    pageHeight: number,
  ): number {
    const nextLineIndex = lineIndex + 1;
    const nextLineHeight = lineHeights[nextLineIndex];
    if (!nextLineHeight) {
      return accumulatedHeight;
    }

    const nextPageHeight = this.calculateNextPageHeight(
      pageHeight,
      accumulatedHeight,
    );

    const isPageBreakNeeded =
      accumulatedHeight + nextLineHeight >
      nextPageHeight - this.pageBreakMargin;
    if (isPageBreakNeeded) {
      return nextPageHeight + this.pageBreakMargin;
    }

    return accumulatedHeight;
  }

  private calculateNextPageHeight(
    pageHeight: number,
    accumulatedHeight: number,
  ): number {
    const activePage = this.getActivePage(accumulatedHeight, pageHeight);
    return pageHeight + activePage * this.pageHeight;
  }

  private getActivePage(accumulatedHeight: number, pageHeight: number): number {
    return !this.pageHeight || accumulatedHeight < pageHeight
      ? 0
      : Math.floor((accumulatedHeight - pageHeight) / this.pageHeight) + 1;
  }
}

const createLineElement = (
  textarea: Textarea,
  text: string,
): fabric.Textbox => {
  return new fabric.Textbox(text ?? "", {
    width: textarea.width,
    fill: textarea.fill,
    fontSize: textarea.fontSize,
    fontFamily: textarea.fontFamily,
    fontWeight: textarea.fontWeight,
    textAlign: textarea.textAlign,
    lineHeight: textarea.lineHeight,
    lockScalingY: textarea.lockScalingY,
    editable: textarea.editable,
  });
};

const calculateLineHeights = (
  lineObjects: fabric.Object[],
  lineHeight: number,
): number[] => {
  return lineObjects.map((obj, lineIndex) => {
    if (!obj.height) {
      return 0;
    }

    const isLastLine = lineIndex === lineObjects.length - 1;
    return obj.height * (isLastLine ? 1 : lineHeight);
  });
};
